import clsx from 'clsx';
import type { InputHTMLAttributes, ReactNode } from 'react';
import { useI18n } from '../lib/i18n';
import { Field, Input } from './ui';

/**
 * A ticket's subject, and the name of a task, a Leaders Task, a Q Task and an
 * announcement, are 25 characters at most; and each of them has a description
 * (operator, 2026-10-12). The API's own rule (`common/record-text.ts`).
 */
export const NAME_MAX = 25;

/** Characters, as the API counts them: once trimmed, an Arabic letter is one, as an English one is. */
export const nameLength = (value: string) => [...value.trim()].length;

/** Whether a name may be saved: something, and no more than 25 characters. */
export const nameFits = (value: string) => {
  const length = nameLength(value);
  return length > 0 && length <= NAME_MAX;
};

/** Whether a description may be saved: something besides spaces. */
export const hasDescription = (value: string | null | undefined) => !!value?.trim();

/**
 * The name field of those forms: it takes no more than 25 characters and counts
 * them as they are typed. One saved before the limit, or brought in longer —
 * from a change request, an announcement, a Q Task — says so, and the form
 * holds Save back (`nameFits`) until it is shortened.
 */
export function NameField({
  label,
  hint,
  value,
  onChange,
  ...input
}: {
  label: ReactNode;
  hint?: ReactNode;
  value: string;
  onChange: (value: string) => void;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>) {
  const { t } = useI18n();
  const length = nameLength(value);
  const over = length > NAME_MAX;
  return (
    <Field
      label={label}
      required
      hint={
        <span className="flex items-start gap-3">
          <span className="min-w-0 flex-1">{hint}</span>
          <span className={clsx('shrink-0 tabular-nums', over && 'text-error-fg dark:text-error-dark')} dir="ltr">
            {length}/{NAME_MAX}
          </span>
        </span>
      }
      error={over ? t('common.nameTooLong', { max: NAME_MAX, count: length }) : null}
    >
      <Input value={value} maxLength={NAME_MAX} onChange={(e) => onChange(e.target.value)} {...input} />
    </Field>
  );
}
